import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { specials } from '../data/specials';
import { getLeadContext, newId } from '../utils/siteTracking';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { generateContactPageSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';
import { PhoneLinkText } from '../components/PhoneLinkText';

const ContactPage = () => {
  const navigate = useNavigate();

  // Generate schemas
  const contactSchema = generateContactPageSchema();
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Contact', url: 'https://comfortmasterheating.com/contact' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([contactSchema, breadcrumbSchema], 'contact');

  const [searchParams] = useSearchParams();
  const claimedOffer = specials.find(s => s.slug === searchParams.get('offer'));

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    zip: '',
    customer_type: '',
    message: claimedOffer ? `I'd like to claim the "${claimedOffer.title}" special${claimedOffer.code ? ` (code ${claimedOffer.code})` : ''}.` : '',
    smsConsent: false,
    website: '' // Honeypot field
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  // Stable per form fill so retries never create duplicate leads
  const submissionId = useRef(newId());

  // Load Cloudflare Turnstile script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAADPWur5UErH2e24o',
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setError('CAPTCHA verification failed. Please try again.'),
          'expired-callback': () => setTurnstileToken(''),
        });
      }
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e) => {
    const { name, type, checked } = e.target;
    const value = name === 'zip' ? e.target.value.replace(/\D/g, '').slice(0, 5) : e.target.value;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot filled = bot: let the server store it as spam, then silently "succeed"
    if (formData.website) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, submission_id: submissionId.current, ...getLeadContext() }),
      }).catch(() => {}).finally(() => navigate('/thank-you'));
      return;
    }

    // Check Turnstile token
    if (!turnstileToken) {
      setError('Please complete the CAPTCHA verification.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
          submission_id: submissionId.current,
          ...getLeadContext()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to thank you page for conversion tracking
        navigate('/thank-you');
      } else {
        const detail = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join(' ') : data.detail;
        setError(detail || 'Something went wrong. Please try again.');
        // Reset Turnstile on error
        if (window.turnstile) {
          window.turnstile.reset();
          setTurnstileToken('');
        }
      }
    } catch (err) {
      setError('Unable to send message. Please call us directly at (859) 357-6803.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <Helmet>
        <title>Contact Us | ComfortMaster Heating & Air | Lexington KY HVAC</title>
        <meta name="description" content="Contact ComfortMaster for HVAC services in Central Kentucky. Get a free estimate for heating, cooling, and air quality services. Call (859) 357-6803." />
        <link rel="canonical" href="https://comfortmasterheating.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-stone-300">
              Ready to get started? Contact us for a free estimate or to schedule service. We're here to help with all your HVAC needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading font-bold text-2xl text-slate-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Phone</h3>
                    <a href="tel:8593576803" className="text-emerald-600 hover:text-emerald-700" data-testid="contact-phone">
                      (859) 357-6803
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Email</h3>
                    <a href="mailto:info@comfortmasterheating.com" className="text-emerald-600 hover:text-emerald-700" data-testid="contact-email">
                      info@comfortmasterheating.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Address</h3>
                    <p className="text-slate-600">
                      51 Wichita Drive<br />
                      Nicholasville, KY 40356
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Office Hours</h3>
                    <p className="text-slate-600">
                      Monday - Friday: 7:30AM - 4:30PM
                    </p>
                    <h3 className="font-bold text-slate-900 mt-3">Emergency Service</h3>
                    <p className="text-slate-600">
                      24/7/365
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Notice */}
              <div className="mt-8 bg-orange-100 border border-orange-200 rounded-xl p-4">
                <h3 className="font-bold text-orange-800 mb-1">24/7 Emergency Service</h3>
                <p className="text-orange-700 text-sm"><PhoneLinkText text="For emergency HVAC issues, call us anytime at (859) 357-6803" /></p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">Get Your Free Estimate</h2>
                <p className="text-slate-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">We've received your request and will contact you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from humans, bots will fill it */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label" htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder="John Smith"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder="john@example.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label" htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="form-input"
                          placeholder="(859) 555-0123"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="service">Service Needed</label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="form-input"
                          data-testid="select-service"
                        >
                          <option value="">Select a service</option>
                          <option value="heating">Heating Services</option>
                          <option value="cooling">Cooling Services</option>
                          <option value="installation">New Installation</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="air-quality">Air Quality</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label" htmlFor="zip">ZIP Code</label>
                        <input
                          type="text"
                          id="zip"
                          name="zip"
                          inputMode="numeric"
                          pattern="[0-9]{5}"
                          maxLength={5}
                          title="5-digit ZIP code"
                          value={formData.zip}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="40356"
                          data-testid="input-zip"
                        />
                      </div>
                      <div>
                        <span className="form-label" id="customer-type-label">Property Type</span>
                        <div className="flex gap-3" role="radiogroup" aria-labelledby="customer-type-label">
                          {['residential', 'commercial'].map(type => (
                            <label key={type} className={`flex-1 flex items-center justify-center gap-2 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${formData.customer_type === type ? 'border-emerald-600 bg-emerald-50 text-emerald-700 font-semibold' : 'border-slate-300 text-slate-600 hover:border-emerald-400'}`}>
                              <input type="radio" name="customer_type" value={type} checked={formData.customer_type === type} onChange={handleChange} className="sr-only" data-testid={`radio-${type}`} />
                              {type === 'residential' ? 'Residential' : 'Commercial'}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="form-label" htmlFor="message">How Can We Help?</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        className="form-input"
                        placeholder="Tell us about your HVAC needs..."
                        data-testid="textarea-message"
                      ></textarea>
                    </div>

                    {/* SMS Consent Checkbox */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="smsConsent"
                        name="smsConsent"
                        checked={formData.smsConsent}
                        onChange={handleChange}
                        required
                        className="mt-1 w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                        data-testid="checkbox-sms-consent"
                      />
                      <label htmlFor="smsConsent" className="text-sm text-slate-600 leading-relaxed">
                        You agree by submitting this form and signing up for texts, you consent to receive text messages from Comfort Master Heating & Air at the number provided, including messages sent by auto dialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available) and no further messages will be sent. Reply HELP for help. Information will not be shared with third parties for marketing or promotional purposes. I Agree to the <Link to="/terms-conditions" className="text-emerald-600 hover:underline" target="_blank">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-emerald-600 hover:underline" target="_blank">Privacy Policy</Link>.
                      </label>
                    </div>

                    {/* Cloudflare Turnstile CAPTCHA */}
                    <div className="flex justify-start">
                      <div ref={turnstileRef} data-testid="turnstile-widget"></div>
                    </div>

                    {error && (
                      <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <p><PhoneLinkText text={error} /></p>
                      </div>
                    )}

                    <button 
                      type="submit" 
                      disabled={loading}
                      className="btn-primary w-full md:w-auto flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                      data-testid="submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send Request
                        </>
                      )}
                    </button>

                    <p className="text-sm text-slate-500">
                      * Includes complimentary second opinions on all estimates
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
